/* eslint-disable react/no-multi-comp */
import React from "react";
import { matchPath, useLocation } from "react-router";
import clsx from "clsx";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
  colors,
  Divider,
  Drawer,
  List,
  ListSubheader,
} from "@material-ui/core";
import firebase from "firebase/app";

import * as Sentry from "@sentry/browser";

import { makeRequest, stopMasquerading, userIsFPEmployee } from "./util";

import NavItem from "./NavItem";
import {
  CommercialIcon,
  CustomerInsightsManagerIcon,
  DatabaseRestoreIcon,
  FilmNoirIcon,
  IncomingDataIcon,
  IndexIcon,
  MulticastIcon,
  OnlineIcon,
  OpenInBrowserIcon,
  SettingsIcon,
  SignOutIcon,
  StaffIcon,
  SwapIcon,
} from "./components/icons/themes/windows10";
import useAccountMetadata from "./hooks/useAccountMetadata";

export function signout() {
  stopMasquerading();
  makeRequest("invalidate_token")
    .then(() => {
      firebase.auth().signOut();
    })
    .catch((error) => {
      Sentry.captureException(error);
      firebase.auth().signOut();
    });
}

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  mobileDrawer: {
    width: 256,
  },
  desktopDrawer: {
    width: 188,
    top: 64,
    height: "calc(100% - 64px)",
  },
  navigation: {
    overflow: "auto",
    padding: theme.spacing(0, 2, 2, 2),
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  profile: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
  },
  badge: {
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  },
  badgeDot: {
    height: 9,
    minWidth: 9,
  },
  onlineBadge: {
    backgroundColor: colors.green[600],
  },
  awayBadge: {
    backgroundColor: colors.orange[600],
  },
  busyBadge: {
    backgroundColor: colors.red[600],
  },
  offlineBadge: {
    backgroundColor: colors.grey[300],
  },
  avatar: {
    cursor: "pointer",
    width: 40,
    height: 40,
  },
  details: {
    marginLeft: theme.spacing(2),
  },
  moreButton: {
    marginLeft: "auto",
    color: colors.blueGrey[200],
  },
}));

function renderNavItems({
  // eslint-disable-next-line react/prop-types
  items,
  subheader,
  key,
  ...rest
}) {
  items = items.filter((item) => !item.shouldShowFn || item.shouldShowFn());

  return (
    <List key={key}>
      {subheader && <ListSubheader disableSticky>{subheader}</ListSubheader>}
      {/* eslint-disable-next-line react/prop-types */}
      {items.reduce(
        // eslint-disable-next-line no-use-before-define
        (acc, item) => reduceChildRoutes({ acc, item, ...rest }),
        []
      )}
    </List>
  );
}

renderNavItems.propTypes = {
  items: PropTypes.array,
};

function reduceChildRoutes({ acc, pathname, item, depth = 0 }) {
  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false,
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        key={item.href}
        label={item.label}
        open={Boolean(open)}
        title={item.title}
        className={item.className}
        activeHrefPrefixes={item.activeHrefPrefixes}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        onClick={item.onClick}
        icon={item.icon}
        key={item.href}
        label={item.label}
        title={item.title}
        activeHrefPrefixes={item.activeHrefPrefixes}
      />
    );
  }

  return acc;
}

function NavBar({ className, ...rest }) {
  const classes = useStyles();
  const location = useLocation();
  const accountMetadata = useAccountMetadata();

  const platformNavItems = [
    {
      title: "Sources",
      href: "/sources",
      icon: IncomingDataIcon,
    },
  ];

  const destinationNavItems = [
    {
      title: "Apps",
      href: "/destinations/apps",
      icon: MulticastIcon,
    },
    {
      title: "Warehouses",
      href: "/destinations/warehouses",
      icon: DatabaseRestoreIcon,
    },
    {
      title: "Advertising",
      href: "/destinations/advertising",
      icon: CommercialIcon,
    },
  ];

  const eventNavItems = [
    {
      title: "Schema",
      href: "/events/schema",
      icon: IndexIcon,
    },
    {
      title: "Live View",
      href: "/events/liveview",
      icon: OnlineIcon,
    },
    {
      title: "Transformations",
      href: "/transformations",
      icon: SwapIcon,
    },
    {
      title: "Visual Editor",
      href: "/events/editor",
      icon: OpenInBrowserIcon,
    },
  ];

  const cohortingNavItems = [
    {
      title: "Audiences",
      href: "/cohorts",
      icon: StaffIcon,
      activeHrefPrefixes: ["/cohort", "/cohorts"],
    },
    {
      title: "Destinations",
      href: "/destinations/cohorting",
      icon: CustomerInsightsManagerIcon,
    },
  ];

  const accountNavItems = [
    {
      title: "Account",
      href: "/settings",
      icon: SettingsIcon,
      items: [
        {
          title: "Team",
          href: "/settings/team",
        },
        {
          title: "Projects",
          href: "/settings/projects",
        },
      ],
    },
    {
      title: "Masquerade",
      href: "/masquerade",
      shouldShowFn: userIsFPEmployee,
      icon: FilmNoirIcon,
    },
    {
      title: "Sign Out",
      icon: SignOutIcon,
      onClick: (e) => {
        e.preventDefault();
        signout();
      },
      href: "/signout",
    },
  ];

  return (
    <Drawer
      anchor="left"
      classes={{
        paper: classes.desktopDrawer,
      }}
      open
      variant="persistent"
    >
      <div {...rest} className={clsx(classes.root, className)}>
        <nav className={classes.navigation}>
          <div>
            {renderNavItems({
              items: platformNavItems,
              subheader: "COLLECT",
              pathname: location.pathname,
            })}
            {renderNavItems({
              items: destinationNavItems,
              subheader: "DESTINATIONS",
              pathname: location.pathname,
            })}
            {renderNavItems({
              items: eventNavItems,
              subheader: "EVENTS",
              pathname: location.pathname,
            })}
            {accountMetadata.config.personas &&
              renderNavItems({
                items: cohortingNavItems,
                subheader: "COHORTS",
                pathname: location.pathname,
              })}
          </div>
          {renderNavItems({
            items: accountNavItems,
            subheader: "ACCOUNT",
            pathname: location.pathname,
          })}
        </nav>
        <Divider className={classes.divider} />
      </div>
    </Drawer>
  );
}

NavBar.propTypes = {
  className: PropTypes.string,
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default NavBar;
